/* eslint-disable */
import React, { useMemo, useState, useEffect } from "react";
import { get } from "lodash";
import { WCAG_ADOPTED_TEXT } from "../../utils/constants";
import themePropTypes from "../theme-prop-types";
import Title from "./title";

const StatusMessage = ({ message, ...props }) => {
  const [messageText, setMessageText] = useState("");

  // To schedule async call for status announcement
  useEffect(() => {
    setTimeout(() => {
      setMessageText(message);
    }, 0);
  }, []);

  return (
    <p role='status' aria-live='polite' {...props}>
      {messageText}
    </p>
  );
};

const SemanticTag = ({
  ariaLive,
  name,
  isLabel,
  isSpan,
  isHeading1,
  isHeading2,
  isHeading3,
  isStatus,
  children,
  params,
  data,
  ...props
}) => {
  if (isSpan) {
    return <span {...props}>{children}</span>;
  }
  if (isLabel) {
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    return (
      <label {...props} htmlFor={name}>
        {children}
      </label>
    );
  }
  if (isHeading1) {
    return <Title title={children} forceFocus />;
  }
  if (isHeading2) {
    return <h2 {...props}>{children}</h2>;
  }
  if (ariaLive) {
    return (
      <p aria-live='assertive' {...props}>
        {children}
      </p>
    );
  }
  if (isHeading3) {
    return <h3 {...props}>{children}</h3>;
  }
  if (isStatus) {
    return <StatusMessage message={children} {...props} />;
  }

  const Tag = get(data, "tag", "p");

  return (
    <Tag
      {...props}
      tabIndex={params?.split("|").includes(WCAG_ADOPTED_TEXT) ? -1 : null}
    >
      {children}
    </Tag>
  );
};

const Text = ({
  accessibility,
  children,
  label,
  ariaLive,
  onClick,
  params,
  name,
  invalid,
  updatedTextColor,
  updatedLayout,
  layout,
  isSpan,
  isLabel,
  isStatus,
  fieldRef,
  image,
  data = {}
}) => {
  const ariaLabel = accessibility && accessibility.label;
  const getUpdatedLayouts = useMemo(
    () =>
      updatedLayout
        ? {
            border: `${
              updatedLayout?.["border-thickness"]
            }px solid ${updatedLayout?.["border-color"]?.replace("0x", "#")}`,
            backgroundColor: `${updatedLayout?.["background-color"]?.replace(
              "0x",
              "#"
            )}`
          }
        : {},
    [updatedLayout]
  );

  const getUpdatedTextColor = useMemo(
    () =>
      updatedTextColor
        ? {
            color: updatedTextColor?.color?.replace("0x", "#")
          }
        : {},
    [updatedTextColor]
  );

  const visibilityClass = layout?.visible === false ? "hidden-field" : "";
  const paramsClass = params ? params.split("|").join(" ") : "";
  const isHeading1 = params
    ? params?.includes("TDFieldOptionPrimaryHeading")
    : false;
  const isHeading2 = params ? params.includes("TDFieldOptionHeading") : false;
  const isHeading3 = params
    ? params.includes("TDFieldOptionSubheading")
    : false;

  const visibleLabel = children || label;

  return visibleLabel ? (
    onClick ? (
      <button
        data-testid={`text-${name}`}
        className={`text-web-sdk text-link-web-sdk ${name} ${paramsClass} ${visibilityClass}`}
        onClick={onClick}
        type='button'
        style={{ ...getUpdatedLayouts, ...getUpdatedTextColor }}
        ref={fieldRef}
      >
        {visibleLabel}
      </button>
    ) : (
      <SemanticTag
        data-testid={`text-${name}`}
        className={`text-web-sdk ${name} ${paramsClass} ${
          invalid ? "invalid" : ""
        } ${visibilityClass} ${image}`}
        style={{ ...getUpdatedLayouts, ...getUpdatedTextColor }}
        isSpan={isSpan}
        isHeading1={isHeading1}
        isHeading2={isHeading2}
        isHeading3={isHeading3}
        isLabel={isLabel}
        name={name}
        ariaLive={ariaLive}
        isStatus={isStatus}
        params={params}
        data={data}
      >
        {ariaLabel &&
        (React.isValidElement(visibleLabel)
          ? ariaLabel !== visibleLabel?.props?.children?.[0]
          : ariaLabel !== visibleLabel) ? (
          <>
            <span aria-hidden='true'>{visibleLabel}</span>
            <span className='websdk-sr-only'>{ariaLabel}</span>
          </>
        ) : (
          visibleLabel
        )}
      </SemanticTag>
    )
  ) : null;
};

Text.propTypes = themePropTypes.text;
SemanticTag.propTypes = themePropTypes.text;
StatusMessage.propTypes = themePropTypes.text;

export default Text;
